import React, { PropsWithChildren, useCallback, useMemo } from 'react';

import {
  ResetPasswordMutation,
  ResetPasswordMutationVariables,
  UserSessionQuery,
  UserSessionQueryVariables,
  ValidateResetPasswordTokenQuery,
  ValidateResetPasswordTokenQueryVariables,
} from './__generated__/types';
import {
  AuthContext,
  AuthContextState,
  AuthenticationState,
  DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
  LoginCredentials,
  LoginResult,
} from './AuthContext';
import { getDomain, LOGOUT_URL } from './globals';
import { GraphQLClient, GraphqlClientResponse, useGraphqlQuery } from './graphql';
import { parseAuthentication, TrackError } from './helpers';

export type AuthContextProviderProps = PropsWithChildren<{
  env: string;
  client: GraphQLClient;
  fetch?: typeof fetch;
  trackError?: TrackError;
}>;

/** TODO: Add documentation */
export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({
  trackError = () => {},
  client,
  fetch: fetchFn = fetch,
  env,
  children,
}) => {
  // TODO: Check if we have cookie so we can possibly skip the network call.
  const queryResult = useGraphqlQuery<UserSessionQuery, UserSessionQueryVariables>(
    client,
    userSessionQuery,
    {},
  );

  const logout: AuthContextState['logout'] = useCallback(() => {
    return fetchFn(`${getDomain(env)}/session/sign-out`, { method: 'GET', credentials: 'include' })
      .then(() => {
        window.location.reload();
      })
      .catch((e) => {
        trackError('logout', e);
        window.location.assign(LOGOUT_URL(env));
      });
  }, [env, fetchFn, trackError]);

  const validateResetPasswordToken = async (resetPasswordToken: string) => {
    try {
      const response: GraphqlClientResponse<ValidateResetPasswordTokenQuery> = await client.query<
        ValidateResetPasswordTokenQuery,
        ValidateResetPasswordTokenQueryVariables
      >(validateResetPasswordTokenQuery, {
        resetPasswordToken,
      });

      return {
        success: !!response?.data?.webPassword?.validateResetPasswordToken?.valid,
      };
    } catch (err) {
      return {
        success: false,
        displayErrorMessage: 'Error Validating Reset Password Token',
      };
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      const response: GraphqlClientResponse<ResetPasswordMutation> = await client.query<
        ResetPasswordMutation,
        ResetPasswordMutationVariables
      >(resetPasswordMutation, {
        resetPasswordToken: token,
        newPassword: password,
      });

      if (!response.error) {
        return {
          success: true,
        };
      }

      return {
        success: false,
      };
    } catch (err) {
      return {
        success: false,
      };
    }
  };

  const verifyLogin: AuthContextState['verifyLogin'] = useCallback(
    (credentials) => callVerifyLogin(env, fetchFn, trackError, credentials),
    [env, fetchFn, trackError],
  );

  const authentication: AuthenticationState = useMemo(
    () =>
      parseAuthentication(
        { data: queryResult.data, loading: queryResult.loading, error: queryResult.error },
        trackError,
      ),
    // trackError isn't needed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryResult.loading, queryResult.data, queryResult.error, logout],
  );

  return (
    <AuthContext.Provider
      value={{
        authentication,
        logout,
        refresh: queryResult.refresh,
        verifyLogin,
        validateResetPasswordToken,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

async function callVerifyLogin(
  env: string,
  fetchFn: typeof fetch,
  trackError: TrackError,
  credentials: LoginCredentials,
): Promise<LoginResult> {
  let payload: unknown;
  if ('googleIdToken' in credentials) {
    payload = { google_id_token: credentials.googleIdToken };
  } else if ('facebookIdToken' in credentials) {
    payload = { facebook_id_token: credentials.facebookIdToken };
  } else {
    payload = {
      human: {
        email: credentials.email,
        password: credentials.password,
      },
    };
  }

  // See `verify_login` at
  // https://github.com/opendoor-labs/web/blob/master/app/controllers/humans/sessions_controller.rb
  let resp;
  try {
    resp = await fetchFn(`${getDomain(env)}/verify_login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    trackError('login', e as Error);
    return { success: false, displayErrorMessage: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE };
  }

  if (resp.ok) {
    return { success: true };
  } else {
    let displayErrorMessage = DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE;
    try {
      const json = await resp.json();
      if ('error' in json) {
        displayErrorMessage = json['error'];
      }
    } catch (e) {
      trackError('login error without error message', e as Error);
    }
    return {
      success: false,
      displayErrorMessage,
    };
  }
}

export const userSessionQuery = /* GraphQL */ `
  query UserSession {
    web {
      userSession {
        human {
          token
          customer_uuid
          email
          first_name
          full_name
          opendoor_internal_access
          primary_phone
          impersonated
        }
      }
    }
  }
`;

export const validateResetPasswordTokenQuery = /* GraphQL */ `
  query validateResetPasswordToken($resetPasswordToken: String!) {
    webPassword {
      validateResetPasswordToken(input: { resetPasswordToken: $resetPasswordToken }) {
        valid
      }
    }
  }
`;

export const resetPasswordMutation = /* GraphQL */ `
  mutation resetPassword($resetPasswordToken: String!, $newPassword: String!) {
    webPassword {
      resetPassword(input: { resetPasswordToken: $resetPasswordToken, newPassword: $newPassword }) {
        _empty
      }
    }
  }
`;
